import { NgIf, ViewportScroller } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    FormGroup,
    FormBuilder,
    Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService, BookingPayload } from '../../../../services/api.service';
import { BookingService } from '../../../../services/booking.service';
import { ButtonComponent } from '../../../common/button/button.component';
import { FormState } from '../../contact-apartment/contact-apartment.component';

@Component({
    selector: 'app-booking-full-form',
    imports: [
        ButtonComponent,
        RouterLink,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        TranslatePipe,
    ],
    templateUrl: './booking-full-form.component.html',
})
export class BookingFullFormComponent {
    formStateEnum = FormState;
    form!: FormGroup;
    bookingService = inject(BookingService);
    private fb: FormBuilder = inject(FormBuilder);
    private apiService: ApiService = inject(ApiService);
    private translate = inject(TranslateService);
    private viewportScroller: ViewportScroller = inject(ViewportScroller);
    private toastService = inject(ToastrService);

    ngOnInit() {
        this.form = this.fb.group({
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(2),
                    Validators.maxLength(100),
                ],
            ],
            lastName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(2),
                    Validators.maxLength(100),
                ],
            ],
            email: ['', [Validators.required, Validators.email]],
            phone: [
                '',
                [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(30),
                ],
            ],
            message: [
                undefined,
                [Validators.minLength(10), Validators.maxLength(500)],
            ],
            street: ['', [Validators.required, Validators.maxLength(100)]],
            houseNumber: ['', [Validators.required, Validators.maxLength(10)]],
            postalCode: ['', [Validators.required, Validators.maxLength(10)]],
            place: ['', [Validators.required, Validators.maxLength(50)]],
            privacyPolicy: [false, Validators.requiredTrue],
        });
    }

    onSubmit() {
        this.bookingService.formState.set(FormState.Submitting);
        if (
            this.form.valid &&
            this.bookingService.startDate() &&
            this.bookingService.endDate()
        ) {
            this.bookingService.config.reload();
            this.apiService
                .createBookingEntry(this.createBookingEntry())
                .subscribe({
                    next: () => {
                        this.form.reset();
                        this.translate
                            .get('booking.bookingFullForm.success')
                            .subscribe((translation) => {
                                this.toastService.success(translation);
                            });
                        this.bookingService.formState.set(FormState.Success);
                    },
                    error: (err) => {
                        if (err.error.error) {
                            switch (err.error.error.message) {
                                case '1':
                                    this.translate
                                        .get(
                                            'booking.bookingFullForm.checkInPast',
                                        )
                                        .subscribe((translation) => {
                                            this.bookingService.errorMessage.set(
                                                translation,
                                            );
                                        });
                                    break;
                                case '2':
                                    this.translate
                                        .get(
                                            'booking.bookingFullForm.checkOutBeforeCheckIn',
                                        )
                                        .subscribe((translation) => {
                                            this.bookingService.errorMessage.set(
                                                translation,
                                            );
                                        });
                                    break;
                                case '3':
                                    this.translate
                                        .get(
                                            'booking.bookingFullForm.minimumStay',
                                            {
                                                nights:
                                                    this.bookingService.config.value()!
                                                        .data.minimumStay - 1,
                                            },
                                        )
                                        .subscribe((translation) => {
                                            this.bookingService.errorMessage.set(
                                                translation,
                                            );
                                        });
                                    break;
                                case '4':
                                    if (
                                        this.bookingService.config.value()!.data
                                            .maximumStay
                                    )
                                        this.translate
                                            .get(
                                                'booking.bookingFullForm.maximumStay',
                                                {
                                                    nights:
                                                        this.bookingService.config.value()!
                                                            .data.maximumStay! -
                                                        1,
                                                },
                                            )
                                            .subscribe((translation) => {
                                                this.bookingService.errorMessage.set(
                                                    translation,
                                                );
                                            });
                                    break;
                                case '5':
                                    this.translate
                                        .get(
                                            'booking.bookingFullForm.reservationConflict',
                                        )
                                        .subscribe((translation) => {
                                            this.bookingService.errorMessage.set(
                                                translation,
                                            );
                                        });
                                    break;
                            }
                        }
                        this.translate
                            .get('booking.bookingFullForm.errorSendingRequest')
                            .subscribe((translation) => {
                                this.toastService.error(translation);
                            });
                        this.bookingService.formState.set(FormState.Error);
                    },
                });
        } else {
            this.translate
                .get('booking.bookingFullForm.invalidForm')
                .subscribe((translation) => {
                    this.toastService.error(translation);
                });
            this.bookingService.formState.set(FormState.Invalid);
        }
    }

    scrollToForm() {
        //only on mobile
        if (window.innerWidth > 768) {
            this.viewportScroller.setOffset([0, 32]);
            this.viewportScroller.scrollToAnchor('reservieren');
        }
    }

    private createBookingEntry(): BookingPayload {
        return {
            data: {
                startDate: this.bookingService.startDate()!.toISOString(),
                endDate: this.bookingService.endDate()!.toISOString(),
                adults: this.bookingService.adultAmount(),
                children: this.bookingService.childrenAmount(),
                infants: this.bookingService.infantsAmount(),
                firstName: this.form.value.firstName,
                lastName: this.form.value.lastName,
                email: this.form.value.email,
                phone: this.form.value.phone,
                message: this.form.value.message,
                street: this.form.value.street,
                houseNumber: this.form.value.houseNumber,
                postalCode: this.form.value.postalCode,
                place: this.form.value.place,
            },
        };
    }
}
